import type { CustomsDocument } from './types';

// ─── Carrier Capabilities ─────────────────────────────────────────────────────

export const GET_CARRIER_DOCUMENT_CAPABILITIES_REQUEST = 'GET_CARRIER_DOCUMENT_CAPABILITIES_REQUEST';
export const getCarrierDocumentCapabilitiesRequest = () => ({
  type: GET_CARRIER_DOCUMENT_CAPABILITIES_REQUEST,
});

export const GET_CARRIER_DOCUMENT_CAPABILITIES_SUCCESS = 'GET_CARRIER_DOCUMENT_CAPABILITIES_SUCCESS';
export const getCarrierDocumentCapabilitiesSuccess = (capabilities: unknown) => ({
  type: GET_CARRIER_DOCUMENT_CAPABILITIES_SUCCESS,
  capabilities,
});

export const GET_CARRIER_DOCUMENT_CAPABILITIES_FAILURE = 'GET_CARRIER_DOCUMENT_CAPABILITIES_FAILURE';
export const getCarrierDocumentCapabilitiesFailure = (error: unknown) => ({
  type: GET_CARRIER_DOCUMENT_CAPABILITIES_FAILURE,
  error,
});

// ─── Order Documents ──────────────────────────────────────────────────────────

export const GET_ORDER_DOCUMENTS_REQUEST = 'GET_ORDER_DOCUMENTS_REQUEST';
export const getOrderDocumentsRequest = (salesOrderId: string) => ({
  type: GET_ORDER_DOCUMENTS_REQUEST,
  salesOrderId,
});

export const GET_ORDER_DOCUMENTS_SUCCESS = 'GET_ORDER_DOCUMENTS_SUCCESS';
export const getOrderDocumentsSuccess = (salesOrderId: string, documents: CustomsDocument[]) => ({
  type: GET_ORDER_DOCUMENTS_SUCCESS,
  salesOrderId,
  documents,
});

export const GET_ORDER_DOCUMENTS_FAILURE = 'GET_ORDER_DOCUMENTS_FAILURE';
export const getOrderDocumentsFailure = (salesOrderId: string, error: unknown) => ({
  type: GET_ORDER_DOCUMENTS_FAILURE,
  salesOrderId,
  error,
});

export const UPLOAD_ORDER_DOCUMENT_REQUEST = 'UPLOAD_ORDER_DOCUMENT_REQUEST';
export const uploadOrderDocumentRequest = (salesOrderId: string, documentType: string, file: File) => ({
  type: UPLOAD_ORDER_DOCUMENT_REQUEST,
  salesOrderId,
  documentType,
  file,
});

export const UPLOAD_ORDER_DOCUMENT_SUCCESS = 'UPLOAD_ORDER_DOCUMENT_SUCCESS';
export const uploadOrderDocumentSuccess = (salesOrderId: string, document: CustomsDocument) => ({
  type: UPLOAD_ORDER_DOCUMENT_SUCCESS,
  salesOrderId,
  document,
});

export const UPDATE_ORDER_UPLOAD_PROGRESS = 'UPDATE_ORDER_UPLOAD_PROGRESS';
export const updateOrderUploadProgress = (salesOrderId: string, percentageComplete: number) => ({
  type: UPDATE_ORDER_UPLOAD_PROGRESS,
  salesOrderId,
  percentageComplete,
});

export const UPLOAD_ORDER_DOCUMENT_FILE_UPLOADED = 'UPLOAD_ORDER_DOCUMENT_FILE_UPLOADED';
export const uploadOrderDocumentFileUploaded = (salesOrderId: string, documentId: string) => ({
  type: UPLOAD_ORDER_DOCUMENT_FILE_UPLOADED,
  salesOrderId,
  documentId,
});

export const UPLOAD_ORDER_DOCUMENT_FAILURE = 'UPLOAD_ORDER_DOCUMENT_FAILURE';
export const uploadOrderDocumentFailure = (salesOrderId: string, error: unknown) => ({
  type: UPLOAD_ORDER_DOCUMENT_FAILURE,
  salesOrderId,
  error,
});

export const DELETE_ORDER_DOCUMENT_REQUEST = 'DELETE_ORDER_DOCUMENT_REQUEST';
export const deleteOrderDocumentRequest = (salesOrderId: string, documentId: string) => ({
  type: DELETE_ORDER_DOCUMENT_REQUEST,
  salesOrderId,
  documentId,
});

export const DELETE_ORDER_DOCUMENT_SUCCESS = 'DELETE_ORDER_DOCUMENT_SUCCESS';
export const deleteOrderDocumentSuccess = (salesOrderId: string, documentId: string) => ({
  type: DELETE_ORDER_DOCUMENT_SUCCESS,
  salesOrderId,
  documentId,
});

export const DELETE_ORDER_DOCUMENT_FAILURE = 'DELETE_ORDER_DOCUMENT_FAILURE';
export const deleteOrderDocumentFailure = (salesOrderId: string, documentId: string, error: unknown) => ({
  type: DELETE_ORDER_DOCUMENT_FAILURE,
  salesOrderId,
  documentId,
  error,
});

// ─── Shipment Documents ───────────────────────────────────────────────────────

export const GET_SHIPMENT_DOCUMENTS_REQUEST = 'GET_SHIPMENT_DOCUMENTS_REQUEST';
export const getShipmentDocumentsRequest = (shipmentId: string) => ({
  type: GET_SHIPMENT_DOCUMENTS_REQUEST,
  shipmentId,
});

export const GET_SHIPMENT_DOCUMENTS_SUCCESS = 'GET_SHIPMENT_DOCUMENTS_SUCCESS';
export const getShipmentDocumentsSuccess = (shipmentId: string, documents: CustomsDocument[]) => ({
  type: GET_SHIPMENT_DOCUMENTS_SUCCESS,
  shipmentId,
  documents,
});

export const GET_SHIPMENT_DOCUMENTS_FAILURE = 'GET_SHIPMENT_DOCUMENTS_FAILURE';
export const getShipmentDocumentsFailure = (shipmentId: string, error: unknown) => ({
  type: GET_SHIPMENT_DOCUMENTS_FAILURE,
  shipmentId,
  error,
});

export const UPLOAD_SHIPMENT_DOCUMENT_REQUEST = 'UPLOAD_SHIPMENT_DOCUMENT_REQUEST';
export const uploadShipmentDocumentRequest = (
  shipmentId: string,
  documentType: string,
  file: File,
  { carrierCode, trackingNumber }: { carrierCode?: string | null; trackingNumber?: string | null } = {},
) => ({
  type: UPLOAD_SHIPMENT_DOCUMENT_REQUEST,
  shipmentId,
  documentType,
  file,
  carrierCode,
  trackingNumber,
});

export const UPLOAD_SHIPMENT_DOCUMENT_SUCCESS = 'UPLOAD_SHIPMENT_DOCUMENT_SUCCESS';
export const uploadShipmentDocumentSuccess = (shipmentId: string, document: CustomsDocument) => ({
  type: UPLOAD_SHIPMENT_DOCUMENT_SUCCESS,
  shipmentId,
  document,
});

export const UPLOAD_SHIPMENT_DOCUMENT_FILE_UPLOADED = 'UPLOAD_SHIPMENT_DOCUMENT_FILE_UPLOADED';
export const uploadShipmentDocumentFileUploaded = (shipmentId: string, documentId: string) => ({
  type: UPLOAD_SHIPMENT_DOCUMENT_FILE_UPLOADED,
  shipmentId,
  documentId,
});

export const UPLOAD_SHIPMENT_DOCUMENT_FAILURE = 'UPLOAD_SHIPMENT_DOCUMENT_FAILURE';
export const uploadShipmentDocumentFailure = (shipmentId: string, error: unknown) => ({
  type: UPLOAD_SHIPMENT_DOCUMENT_FAILURE,
  shipmentId,
  error,
});

export const DELETE_SHIPMENT_DOCUMENT_REQUEST = 'DELETE_SHIPMENT_DOCUMENT_REQUEST';
export const deleteShipmentDocumentRequest = (shipmentId: string, documentId: string) => ({
  type: DELETE_SHIPMENT_DOCUMENT_REQUEST,
  shipmentId,
  documentId,
});

export const DELETE_SHIPMENT_DOCUMENT_SUCCESS = 'DELETE_SHIPMENT_DOCUMENT_SUCCESS';
export const deleteShipmentDocumentSuccess = (shipmentId: string, documentId: string) => ({
  type: DELETE_SHIPMENT_DOCUMENT_SUCCESS,
  shipmentId,
  documentId,
});

export const DELETE_SHIPMENT_DOCUMENT_FAILURE = 'DELETE_SHIPMENT_DOCUMENT_FAILURE';
export const deleteShipmentDocumentFailure = (shipmentId: string, documentId: string, error: unknown) => ({
  type: DELETE_SHIPMENT_DOCUMENT_FAILURE,
  shipmentId,
  documentId,
  error,
});

export const SEND_SHIPMENT_DOCUMENTS_REQUEST = 'SEND_SHIPMENT_DOCUMENTS_REQUEST';
export const sendShipmentDocumentsRequest = (shipmentId: string) => ({
  type: SEND_SHIPMENT_DOCUMENTS_REQUEST,
  shipmentId,
});

export const SEND_SHIPMENT_DOCUMENTS_SUCCESS = 'SEND_SHIPMENT_DOCUMENTS_SUCCESS';
export const sendShipmentDocumentsSuccess = (shipmentId: string) => ({
  type: SEND_SHIPMENT_DOCUMENTS_SUCCESS,
  shipmentId,
});

export const SEND_SHIPMENT_DOCUMENTS_FAILURE = 'SEND_SHIPMENT_DOCUMENTS_FAILURE';
export const sendShipmentDocumentsFailure = (shipmentId: string, error: unknown) => ({
  type: SEND_SHIPMENT_DOCUMENTS_FAILURE,
  shipmentId,
  error,
});

// ─── Pending Document Config ─────────────────────────────────────────────────

export const SAVE_PENDING_DOCUMENT_CONFIG_REQUEST = 'SAVE_PENDING_DOCUMENT_CONFIG_REQUEST';
export const savePendingDocumentConfigRequest = (fulfillmentPlanId: string, pendingDocument: boolean) => ({
  type: SAVE_PENDING_DOCUMENT_CONFIG_REQUEST,
  fulfillmentPlanId,
  pendingDocument,
});
